import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ThumbsUp, Heart, HandHeart, Laugh, MessageCircle, Trash2, Shield, CornerDownRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Linkify } from "@/components/messages/shared";
import ReportButton from "@/components/ReportButton";
import { Avatar } from "@/components/Avatar";
import { timeAgo } from "@/lib/timeago";

export const REACTIONS = [["like", ThumbsUp, "Gosto"], ["love", Heart, "Adoro"], ["support", HandHeart, "Apoio"], ["laugh", Laugh, "Riso"]];
const errOf = (e) => formatApiError(e.response?.data?.detail) || "Erro";

// Mesmo UI para comunidades e perfis: só mudam os endpoints e as mensagens de bloqueio.
export const SOCIAL_API = {
  community: {
    toggle: "/community-reactions/toggle", mine: "/community-reactions/mine",
    comments: (pid) => `/community-posts/${pid}/comments`, comment: (cid) => `/community-comments/${cid}`, hide: (cid) => `/community-comments/${cid}/hide`,
    reportType: "community_comment", lockedReact: "Entra na comunidade para reagir", lockedComment: "Entra na comunidade para comentar.",
  },
  profile: {
    toggle: "/profile-reactions/toggle", mine: "/profile-reactions/mine",
    comments: (pid) => `/profile/posts/${pid}/comments`, comment: (cid) => `/profile-comments/${cid}`, hide: (cid) => `/admin/profile-comments/${cid}/hide`,
    reportType: "profile_comment", lockedReact: "Confirma o teu e-mail para reagir", lockedComment: "Confirma o teu e-mail para comentar.",
  },
};

export function ReactionBar({ targetType, targetId, reactions, mine, canReact, onChange, compact, base = "community" }) {
  const cfg = SOCIAL_API[base];
  const [state, setState] = useState({ reactions: reactions || {}, mine: mine ?? null });
  useEffect(() => setState({ reactions: reactions || {}, mine: mine ?? null }), [reactions, mine]);
  const toggle = async (type) => {
    if (!canReact) { toast.info(cfg.lockedReact); return; }
    try { const { data } = await api.post(cfg.toggle, { target_type: targetType, target_id: targetId, type }); setState({ reactions: data.reactions, mine: data.mine }); onChange?.(data); }
    catch (e) { toast.error(errOf(e)); }
  };
  return (
    <div className={`flex items-center ${compact ? "gap-1" : "gap-2"}`} data-testid={`reactions-${targetId}`}>
      {REACTIONS.map(([k, Icon, label]) => (
        <button key={k} onClick={() => toggle(k)} title={label} aria-label={label} data-testid={`reaction-${k}-${targetId}`} data-active={state.mine === k}
          className={`inline-flex items-center gap-0.5 rounded-full px-2 text-xs transition-colors data-[active=true]:bg-red-50 data-[active=true]:text-[#c8102e] text-zinc-500 hover:bg-zinc-100 ${compact ? "py-1" : "min-h-[36px]"}`}>
          <Icon size={compact ? 12 : 14} />{state.reactions?.[k] ? <span>{state.reactions[k]}</span> : null}
        </button>
      ))}
    </div>
  );
}

function CommentBody({ c, isReply, user, canInteract, isStaff, isPostOwner, mine, setReplyTo, act, cfg, base }) {
  const canDelete = user && (user.id === c.author_id || isStaff || isPostOwner);
  return (
    <div className="flex items-start gap-2">
      <Avatar src={c.author_photo_url} name={c.author_name} size={28} testId={`comment-avatar-${c.id}`} />
      <div className="flex-1 min-w-0">
        <div className="text-xs"><span className="font-semibold">{c.author_name}</span> <span className="text-zinc-400">· {timeAgo(c.created_at)}{c.edited_at ? " · editado" : ""}</span></div>
        <p className="text-sm whitespace-pre-wrap break-words" data-testid={`comment-text-${c.id}`}><Linkify text={c.text} /></p>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-zinc-500">
          <ReactionBar targetType="comment" targetId={c.id} reactions={c.reactions} mine={mine[c.id]} canReact={canInteract} compact base={base} />
          {!isReply && canInteract && <button onClick={() => setReplyTo(c)} data-testid={`comment-reply-${c.id}`} className="inline-flex items-center gap-0.5"><CornerDownRight size={11} /> Responder</button>}
          {canDelete && <button onClick={() => act(() => api.delete(cfg.comment(c.id)))} data-testid={`comment-delete-${c.id}`} className="inline-flex items-center gap-0.5"><Trash2 size={11} /> Apagar</button>}
          {isStaff && user?.id !== c.author_id && <button onClick={() => act(() => api.post(cfg.hide(c.id)))} data-testid={`comment-hide-${c.id}`} className="inline-flex items-center gap-0.5"><Shield size={11} /> Ocultar</button>}
          {user && user.id !== c.author_id && <span className="ml-auto"><ReportButton target_type={cfg.reportType} target_id={c.id} /></span>}
        </div>
      </div>
    </div>
  );
}

function CommentReplyRow(props) {
  return <div className="ml-8 border-l-2 border-zinc-100 pl-2 py-2" data-testid={`comment-${props.c.id}`}><CommentBody {...props} isReply /></div>;
}

function CommentRow(props) {
  return (
    <div className="py-2" data-testid={`comment-${props.c.id}`}>
      <CommentBody {...props} />
      {props.c.replies?.map((r) => <CommentReplyRow key={r.id} {...props} c={r} />)}
    </div>
  );
}

export function Comments({ post, user, canInteract, isStaff, isPostOwner = false, onCount, base = "community", contextLabel = null }) {
  const cfg = SOCIAL_API[base];
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [mine, setMine] = useState({});
  const load = async (reset = true) => {
    const { data } = await api.get(cfg.comments(post.id), { params: reset ? {} : { cursor } });
    const list = reset ? data.items : [...items, ...data.items];
    setItems(list); setCursor(data.next_cursor);
    if (user) {
      const ids = list.flatMap((c) => [c.id, ...(c.replies || []).map((r) => r.id)]);
      if (ids.length) api.get(cfg.mine, { params: { comment_ids: ids.join(",") } }).then((r) => setMine(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(true); }, [post.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    try { await api.post(cfg.comments(post.id), { text, parent_id: replyTo?.id || null }); setText(""); setReplyTo(null); await load(true); onCount?.(); }
    catch (e) { toast.error(errOf(e)); }
  };
  const act = async (fn) => { try { await fn(); await load(true); onCount?.(); } catch (e) { toast.error(errOf(e)); } };
  return (
    <div className="mt-3 border-t border-zinc-100 pt-2" data-testid={`comments-${post.id}`}>
      {contextLabel && <p className="text-[11px] text-zinc-500 mb-1" data-testid={`comments-context-${post.id}`}>Comentários em: <b>{contextLabel}</b> · separados dos outros destinos</p>}
      {items.length === 0 && <p className="text-xs text-zinc-400 italic py-1">Sem comentários ainda.</p>}
      {items.map((c) => <CommentRow key={c.id} c={c} user={user} canInteract={canInteract} isStaff={isStaff} isPostOwner={isPostOwner} mine={mine} setReplyTo={setReplyTo} act={act} cfg={cfg} base={base} />)}
      {cursor && <button onClick={() => load(false)} className="text-xs text-zinc-600 underline" data-testid={`comments-more-${post.id}`}>Ver mais comentários</button>}
      {canInteract ? (
        <div className="mt-2">
          {replyTo && <div className="text-[11px] text-zinc-500 mb-1 flex items-center gap-2" data-testid="comment-replying-to">A responder a <b>{replyTo.author_name}</b> <button onClick={() => setReplyTo(null)} className="underline">cancelar</button></div>}
          <div className="flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder={replyTo ? "Escreve a tua resposta…" : "Escreve um comentário…"} data-testid={`comment-input-${post.id}`}
              className="flex-1 text-base sm:text-sm border border-zinc-200 rounded-xl px-3 py-2 outline-none focus:border-[#c8102e]" onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) submit(); }} />
            <button onClick={submit} disabled={!text.trim()} data-testid={`comment-submit-${post.id}`} className="px-3 py-2 rounded-xl bg-[#c8102e] text-white text-xs font-semibold disabled:opacity-40">Enviar</button>
          </div>
        </div>
      ) : <p className="text-[11px] text-zinc-400 mt-1">{user ? cfg.lockedComment : "Inicia sessão para comentar."}</p>}
    </div>
  );
}

export function CommentsToggle({ count, open, onToggle, postId }) {
  return (
    <button onClick={onToggle} data-testid={`comments-toggle-${postId}`} aria-expanded={open} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 min-h-[36px] px-1">
      <MessageCircle size={14} /> {count || 0} {count === 1 ? "comentário" : "comentários"}{open ? " ▴" : ""}
    </button>
  );
}
